import * as React from 'react'
import { ChevronRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { StatusPill } from '@/components/ui/status-pill'

/*
 * SetupAreaCard: one Setup area on the Setup index. The whole card is a single
 * link; its accessible name is the area name and description, and the topics
 * line and any audience pill describe it. No counts, badges or licence states.
 */
export function SetupAreaCard({
  to,
  name,
  description,
  accessibleName,
  topics,
  audience,
}: {
  to: string
  name: string
  description: string
  /** The catalogue's "{name}, {description}" sentence, so the order follows the locale. */
  accessibleName: string
  topics?: string
  /** Who can see this area, e.g. "Landlords only". */
  audience?: string
}) {
  const id = React.useId()
  const topicsId = topics ? `${id}-topics` : undefined
  const audienceId = audience ? `${id}-audience` : undefined
  const describedBy = [audienceId, topicsId].filter(Boolean).join(' ') || undefined

  return (
    <Link
      to={to}
      aria-label={accessibleName}
      aria-describedby={describedBy}
      className="flex h-full items-start gap-3 rounded-md border border-line bg-surface px-5 py-4 transition-colors hover:bg-surface-hover"
    >
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="inline-flex flex-wrap items-center gap-2">
          <span className="text-lg text-fg-strong wrap-anywhere">{name}</span>
          {audience ? (
            <span id={audienceId}>
              <StatusPill tone="neutral">{audience}</StatusPill>
            </span>
          ) : null}
        </span>
        <span className="text-sm text-fg-muted">{description}</span>
        {topics ? (
          <span id={topicsId} className="text-xs text-fg-muted">
            {topics}
          </span>
        ) : null}
      </span>
      <ChevronRight aria-hidden="true" className="mt-1 size-5 shrink-0 text-fg-muted" />
    </Link>
  )
}
